import type { CaseDto } from "@repo/data-store";
import { DataList } from "@repo/ui/components/data-list";
import { EmptyState } from "@repo/ui/components/empty-state";
import { Link } from "@tanstack/react-router";
import { FolderOpen } from "lucide-react";
import type { ReactNode } from "react";
import { CaseStatusBadge } from "@/features/cases/components/case-status-badge";
import { formatDate } from "@/shared/lib/date-formats";

function Root({ children }: { children: ReactNode }) {
  return <div className="space-y-3">{children}</div>;
}

function Header({ title = "Cases" }: { title?: string }) {
  return <h2 className="text-sm font-semibold">{title}</h2>;
}

function Body({ children }: { children: ReactNode }) {
  return <DataList>{children}</DataList>;
}

function Empty() {
  return (
    <div className="flex flex-1 flex-col">
      <EmptyState className="m-auto">
        <EmptyState.Icon>
          <FolderOpen className="size-4" />
        </EmptyState.Icon>
        <EmptyState.Content>
          <EmptyState.Title>No cases yet</EmptyState.Title>
          <EmptyState.Description>Cases will appear here as this patient's care progresses.</EmptyState.Description>
        </EmptyState.Content>
      </EmptyState>
    </div>
  );
}

interface ItemProps {
  case: CaseDto;
  /** Patient id for building the detail link. */
  patientId: string;
}

function Item({ case: caseModel, patientId }: ItemProps) {
  const status = caseModel.status;
  const diagnosisCount = caseModel.diagnosis?.length ?? 0;
  const subtitle =
    diagnosisCount === 0 ? "No diagnoses" : `${diagnosisCount} diagnos${diagnosisCount === 1 ? "is" : "es"}`;

  const meta = caseModel.period?.end
    ? `Ended ${formatDate(caseModel.period.end)}`
    : caseModel.period?.start
      ? `Started ${formatDate(caseModel.period.start)}`
      : undefined;

  const title = caseModel.type?.[0]?.display ?? "Case";

  return (
    <DataList.NavigatingRow
      label={title}
      renderLink={(props) => (
        <Link {...props} to="/patients/$patientId/cases/$caseId" params={{ patientId, caseId: caseModel.caseId }} />
      )}
    >
      <DataList.Primary>
        <DataList.Title>{title}</DataList.Title>
        <DataList.Subtitle>{subtitle}</DataList.Subtitle>
      </DataList.Primary>
      <DataList.Status>
        <CaseStatusBadge status={status} />
      </DataList.Status>
      <DataList.Meta>{meta}</DataList.Meta>
    </DataList.NavigatingRow>
  );
}

export const CaseList = Object.assign(Root, {
  Header,
  Body,
  Empty,
  Item,
});
